import styles from "./TermsOfUse.module.css";

const TermsAndConditions = () => {
    return (
        <main className={styles.pages}>
            <h1>Ask Ethos Terms of Use</h1>
            <p>
                <strong>Effective Date: January 22, 2026</strong>
            </p>
            <p>
                Welcome to Ask Ethos &ndash; your digital companion for ethics related inquiries and support.&nbsp; Ask Ethos is an AI-driven chatbot enabled by
                Microsoft Azure OpenAI Service.
            </p>
            <p>
                These Terms of Use ("Terms") govern your use of Ask Ethos (the "Service") operated by Strategi Consulting, LLC.&nbsp; Please read these Terms
                carefully before using the Service.
            </p>
            <p>By accessing or using the Service, you agree to be bound by these Terms.</p>
            <p>We reserve the right to update, modify, or discontinue the Service and communicate how users will be notified of such changes.</p>
            <p>
                If you reside in the European Economic Area, Switzerland, or the UK, you may not access this Service.&nbsp; If you choose to access this Service
                despite this restriction, you do so at your own discretion and agree to waive all rights under GDPR.
            </p>
            <ul>
                <li>
                    <strong>Access and Use:</strong>
                    <ol>
                        <li>
                            The Service is provided for use in accordance with these Terms and Microsoft&rsquo;s&nbsp;
                            <a href="https://www.microsoft.com/licensing/terms/product/ForOnlineServices/all#:~:text=all%20other%20rights.-,Acceptable%20Use%20Policy,-Neither%20Customer%2C%20nor">
                                Acceptable Use Policy
                            </a>
                            &nbsp; for Online Services and the&nbsp;
                            <a href="https://learn.microsoft.com/en-us/legal/cognitive-services/openai/code-of-conduct">Azure OpenAI Code of Conduct</a>&nbsp;
                            (hereafter incorporated as part of these &ldquo;Terms&rdquo;).
                        </li>
                        <li>
                            Minimum Age. You must be at least 13 years old or the minimum age required in your country to consent to use the Services. If you
                            are under 18 you must have your parent or legal guardian&rsquo;s permission to use the Services.
                        </li>
                        <li>Excessive use of the Service may result in temporary throttling of a user&rsquo;s access.</li>
                        <li>We reserve the right to restrict access to any user, for any reason, at any time.</li>
                    </ol>
                </li>
                <li>
                    <strong>Accuracy of Information:</strong>
                    <ol>
                        <li>
                            The Service provides information curated directly from official government websites. While efforts are made to ensure accuracy,
                            information may not always be up-to-date or complete.
                        </li>
                        <li>
                            Users are encouraged to verify critical information with official sources and contact the appropriate individual or individuals
                            responsible for maintaining the accuracy of the information housed on the website.
                        </li>
                        <li>
                            The person or organization originating any information or content, whether publicly posted or privately transmitted through the
                            Service, bears sole responsibility. Access to such information and content is at your own risk, and we disclaim liability for any
                            errors, omissions, damages, or loss you may experience in connection with it. We lack control and are not obligated to intervene in
                            your interpretation, use, or actions resulting from exposure to the content. By using the Service, you release us from any liability
                            related to your acquisition or non-acquisition of content through it.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Informational Use Disclaimer:</strong>
                    <ol>
                        <li>
                            The Service is for educational and informational purposes only and should not be construed as professional advice. It does not
                            provide legal, medical, financial, or any other professional advice.
                        </li>
                        <li>
                            Nothing produced by the Service is a substitute for the competent advice of a licensed professional. Seek professional advice before
                            relying on information contained in this website.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Security and Confidentiality:</strong>
                    <ol>
                        <li>
                            This Service relies on public, open-source information. Do not input any personally identifiable information, sensitive, controlled
                            unclassified, or classified information into the Service.
                        </li>
                        <li>
                            We implement security measures designed to protect your information from unauthorized access. For more information on what is
                            collected and how we use and secure your information please read the current Ask Ethos <a href="#/privacy_policy">Privacy Policy</a>
                            .
                        </li>
                        <li>
                            As part of providing the Azure OpenAI service, Microsoft will temporarily store Inputs and Output Content, solely for debugging and
                            to monitor for and prevent abusive or harmful uses or outputs of the service. Authorized Microsoft employees may review such data
                            that has triggered its automated systems to investigate and verify potential abuse.
                        </li>
                        <li>
                            We will not make available your prompts (inputs) and completions (outputs) to Microsoft, OpenAI, or any other third party outside of
                            the United States Department of Defense.
                        </li>
                        <li>
                            Strategi Consulting, LLC will not make available any information beyond its obligations under its agreements with its customers or
                            Microsoft.
                        </li>
                        <li>
                            Strategi Consulting, LLC will collect and store only a limited amount of data for the purpose of providing analytics to its
                            customer. For more information on what information may be collected, see our Privacy Policy.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Prohibited Conduct:</strong>
                    <ol>
                        <li>Users must not use the Service for any unlawful or malicious purpose.</li>
                        <li>Do not attempt to disrupt, damage, or gain unauthorized access to the Service or associated systems.</li>
                        <li>
                            Users may not use the Service to discover any underlying components of the models, algorithms, and systems, such as exfiltrating the
                            weights of models of the Service or its associated systems and components.
                        </li>
                        <li>
                            We will not use&nbsp;
                            <a href="https://www.microsoft.com/licensing/terms/product/Glossary/all#:~:text=Clustered%20HPC%20Applications.-,Input,-means%20all%20Customer">
                                Input
                            </a>
                            &nbsp; or&nbsp;
                            <a href="https://www.microsoft.com/licensing/terms/product/Glossary/all#:~:text=more%20Virtual%20OSEs.-,Output%20Content,-means%20any%20data">
                                Output Content
                            </a>
                            &nbsp; to train, retrain, or improve any third party foundation models. Users of the Service may not use Output content to create,
                            train, or improve (directly or indirectly) a similar or competing product or service.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Warning Regarding Content:</strong>
                    <ol>
                        <li>
                            The Service will produce content based on the prompts you supply. The specific content generated by the Service in response to your
                            prompts cannot be anticipated in advance. The Service may generate content that you find offensive or objectionable. By agreeing to
                            these Terms and utilizing the Service, you acknowledge and assume the risk that the prompts you provide may lead to the generation
                            of content that you consider offensive or objectionable.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Intellectual Property:</strong>
                    <ol>
                        <li>Users may not reproduce, modify, or distribute the Service&rsquo;s content without proper authorization.</li>
                        <li>The Ask Ethos name and logos are trademarks and service marks of Strategi Consulting, LLC.</li>
                        <li>Strategi Consulting, LLC does not own the Output content of the Service.</li>
                    </ol>
                </li>
                <li>
                    <strong>Limitation of Liability:</strong>
                    <ol>
                        <li>
                            Neither Strategi Consulting, LLC nor Ask Ethos and its affiliates are responsible for any direct, indirect, or consequential damages
                            arising from the use of the Service.
                        </li>
                        <li>The Service is provided "as-is" without any warranty of any kind, either expressed or implied.</li>
                    </ol>
                </li>
                <li>
                    <strong>Third-Party Liability for Violation of Terms:</strong>
                    <ol>
                        <li>
                            Users accessing or using the Service shall be held responsible and liable for any damages, losses, or liabilities incurred by
                            Strategi Consulting, LLC as a result of their violation of the Terms and conditions set forth.
                        </li>
                        <li>
                            In the event that users or a third party breaches or violates any provision of these Terms or any applicable laws, regulations, or
                            rights of third parties while using the service, including but not limited to unauthorized access, misuse, or distribution of
                            content, the said user or third party shall indemnify and hold harmless Strategi Consulting, LLC, its affiliates, officers,
                            directors, employees, agents, and licensors from and against any and all claims, damages, losses, liabilities, costs, and expenses,
                            including legal fees and expenses arising out of or in connection with such breach or violation.
                        </li>
                        <li>
                            Strategi Consulting, LLC reserves the right to take appropriate legal action against any user or third party who violates these
                            Terms or infringes upon the rights of Strategi Consulting, LLC or any other party.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Changes to Service:</strong>
                    <ol>
                        <li>
                            We reserve the right to suspend or terminate specific aspects of the Service, introduce new features, impose limitations on certain
                            features, or restrict access to parts or the entirety of the Service.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Modification of Terms:</strong>
                    <ol>
                        <li>
                            Strategi Consulting, LLC reserves the right to modify these Terms at any time. Continued use of the Service constitutes acceptance
                            of the modified terms.
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Termination of Access:</strong>
                    <ol>
                        <li>Strategi Consulting, LLC reserves the right to terminate access to the Service for users who violate these Terms.</li>
                    </ol>
                </li>
                <li>
                    <strong>Governing Law:</strong>
                    <ol>
                        <li>
                            These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard
                            to its conflict of law principles.
                        </li>
                        <li>
                            Any legal action, suit, or proceeding arising out of or relating to these Terms or the use of the Service shall be instituted in the
                            federal or state courts located within the State of Maryland. You expressly consent and submit to the personal jurisdiction of such
                            courts for the purposes of any such legal action, suit, or proceeding.
                        </li>
                        <li>
                            By accessing or using the Service, you agree that the laws of the State of Delaware will govern the interpretation and enforcement
                            of these Terms, with the understanding that the Service is intended for use primarily across the United States.
                        </li>
                        <li>
                            If any provision of these Terms is found to be invalid by any court having competent jurisdiction, the invalidity of such provision
                            shall not affect the validity of the remaining provisions of these Terms, which shall remain in full force and effect.
                        </li>
                    </ol>
                </li>
                <li>
                    <strong>Contact Information:</strong>
                    <ol>
                        <li>
                            For questions or concerns regarding these Terms of Use, please contact <a href="mailto:info@askethos.ai">info@askethos.ai</a>.
                        </li>
                    </ol>
                </li>
            </ul>
        </main>
    );
};

export default TermsAndConditions;
